/**
 * Edit a published report's title, description and visibility from the
 * dashboard (publish-report spec, Req 6.1, 6.2). The visibility options follow
 * the same rules as publishing (Req 2.2) applied to the report's own team; the
 * server re-checks both the manage rule and the new visibility.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { generateAuditedClient } from "../../../../audit/auditedClient";
import { publishReport } from "../../../../graphql/mutations";
import type {
  PublishedReportSummary,
  PublishReportInput,
} from "../../../../models/API";
import {
  allowedVisibilities,
  ReportVisibility,
  TITLE_MAX_LENGTH,
  validateTitle,
  visibilityFlags,
  visibilityOf,
  VISIBILITY_LABELS,
} from "../../../publishedReports/publishedReportModel";

interface EditPublishedReportDialogProps {
  open: boolean;
  onClose: () => void;
  report: PublishedReportSummary;
  userId: number;
  effectiveRole: string | undefined;
  onSaved: (updated: PublishedReportSummary) => void;
}

function errorMessageOf(err: any, fallback: string): string {
  const message = err?.errors?.[0]?.message ?? err?.message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
}

const EditPublishedReportDialog: React.FC<EditPublishedReportDialogProps> = ({
  open,
  onClose,
  report,
  userId,
  effectiveRole,
  onSaved,
}) => {
  const [title, setTitle] = useState(report.title);
  const [description, setDescription] = useState(report.description ?? "");
  const [visibility, setVisibility] = useState<ReportVisibility>(
    visibilityOf(report)
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle(report.title);
      setDescription(report.description ?? "");
      setVisibility(visibilityOf(report));
      setError(null);
    }
  }, [open, report]);

  const titleError = validateTitle(title);
  // The current visibility is always offered (keeping it is not a change);
  // moving to another one follows the publish rules for the report's team.
  const allowed = allowedVisibilities(effectiveRole, report.team_id);
  const current = visibilityOf(report);

  const handleSave = async () => {
    if (titleError || busy) return;
    setBusy(true);
    setError(null);
    try {
      const client = generateAuditedClient();
      const input: PublishReportInput = {
        id: report.id,
        team_id: report.team_id,
        user_id: userId,
        title: title.trim(),
        description: description.trim() || null,
        ...visibilityFlags(visibility),
      };
      const result = await client.graphql({
        query: publishReport,
        variables: { input },
      });
      const updated = result.data.publishReport;
      if (!updated) throw new Error("No report returned");
      onSaved(updated);
    } catch (err) {
      console.error("[EditPublishedReportDialog] save failed:", err);
      setError(
        errorMessageOf(err, "Could not update this report. Please try again.")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit report</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            error={!!titleError}
            helperText={
              titleError ??
              `${title.trim().length}/${TITLE_MAX_LENGTH} characters`
            }
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControl component="fieldset">
            <FormLabel
              component="legend"
              sx={{ fontWeight: 500, fontSize: "0.875rem" }}
            >
              Visibility
            </FormLabel>
            <RadioGroup
              value={visibility}
              onChange={(e) =>
                setVisibility(e.target.value as ReportVisibility)
              }
              aria-label="visibility"
            >
              {(["private", "public", "global"] as ReportVisibility[]).map(
                (v) => {
                  const enabled = v === current || allowed.includes(v);
                  const control = (
                    <FormControlLabel
                      key={v}
                      value={v}
                      control={<Radio size="small" disabled={!enabled} />}
                      label={VISIBILITY_LABELS[v]}
                      disabled={!enabled}
                    />
                  );
                  if (enabled) return control;
                  return (
                    <Tooltip
                      key={v}
                      title={
                        v === "global"
                          ? "Only FINN team operators/admins can make reports global"
                          : "Only operators/admins can make reports public"
                      }
                      placement="right"
                    >
                      <span>{control}</span>
                    </Tooltip>
                  );
                }
              )}
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={busy || !!titleError}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPublishedReportDialog;
